import React, { forwardRef, useContext } from 'react';
import AppBar from '@material-ui/core/AppBar';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import AppsIcon from '@material-ui/icons/Apps';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import CloseIcon from '@material-ui/icons/Close';
import DescriptionIcon from '@material-ui/icons/Description';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import HelpIcon from '@material-ui/icons/Help';
import MailIcon from '@material-ui/icons/Mail';
import MenuIcon from '@material-ui/icons/Menu';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import { makeStyles } from '@material-ui/styles';
import { colors } from '@skyslope/mache';
import cs from 'classnames';
import { useHistory } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { SkyslopeAppsMenuItems } from '@/common/navigation/SkyslopeAppsMenu';
import CollapseOnScroll from '../../common/CollapseOnScroll';
import { type LaunchDarklyFlags, withLaunchDarkly } from '../../common/launchDarkly';
import { useIsSkySlopeMobileApp } from '../../common/utils';
import { HeaderContext } from '../../context/header-context';
import { useSearchParams } from '../../hooks/use-search-params';
import digiSign from '../../images/digisign.svg';
import skyslopeLogo from '../../images/skyslope_logo.svg';
import { EnvelopeStatuses, ResourceNames } from '../../lib/constants';
import { useIsSmallScreenMediaQuery } from '../../lib/isSmallScreen';
import { useDispatch, useShallowSelector } from '../../lib/reduxHooks';
import { UserOrigins } from '../../lib/types';
import { handleAppLogout } from '../../lib/utils';
import validateBlocksAndSendEnvelope from '../../lib/validateBlocksAndSendEnvelope';
import type { IRootState } from '../../store';
import type { TrialExtension } from '../../store/pageFrame/types';
import * as actions from '../../store/senderBuild/actions';
import { EditEmailModal } from '../senderBuild/EditEmailModal';
import { confirmNavigationAwayFromPage } from '../senderBuild/footer-controls/use-sure-you-want-to-leave-dialog';
import { CancelledSubscriptionBanner } from './CancelledSubscriptionBanner';
import mergeRefs from './merge-refs';
import MobileRecipientsHeader from './MobileRecipientsHeader';
import TrialPeriodBanner from './TrialBanner';

const useStyles = makeStyles(
  () => ({
    headerMargin: {
      width: '100%',
      height: '80px',
    },
    titleRoot: {
      paddingLeft: '32px',
      '& h2': {
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        alignItems: 'center',
      },
    },
    listItemText: {
      '& span': {
        fontSize: '19px',
        fontWeight: 800,
      },
    },
    actions: {
      display: 'flex',
      justifyContent: 'flex-start',
      padding: '16px 32px',
      borderTop: `1px solid ${colors.grey[400]}`,
      color: `${colors.grey[800]}`,
    },
    listItemRoot: {
      '& hover': {
        cursor: 'pointer',
      },
    },
    activeSection: {
      backgroundColor: `${colors.blue[50]}`,
    },
    menuItem: {
      height: '40px',
      display: 'flex',
      paddingRight: '16px',
    },
    menuItemRoom: {
      padding: '0 16px 0 0',
      minHeight: '0',
    },
    itemContainer: {
      marginLeft: '16px',
      paddingLeft: '16px',
      borderRadius: '3px',
      marginBottom: '2px',
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      height: '40px',
    },
    activeText: {
      '& span': {
        fontWeight: 800,
        color: colors.blue[800],
      },
    },
  }),
  { name: 'Header' }
);

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const headerSelector = (state: IRootState) => ({
  settings: state.pageFrame.settings,
  resource: state.senderBuild.resource,
  emailConfig: state.senderBuild.emailConfig,
  envelopeId: state.senderBuild.id,
  envelopeStatus: state.senderBuild.status,
  blocks: state.senderBuild.blocks,
  blockGroups: state.senderBuild.groups,
  id: state.senderBuild.id,
  saving: state.senderBuild.saving,
  readyToSendModalOpen: state.senderBuild.readyToSendModalOpen,
  envelopeSent: state.senderBuild.done,
  zoom: state.senderBuild.zoom,
});

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const Header = (props: {
  setHeaderSize: (size: number) => void;
  redirectHeader?: boolean;
  flags: LaunchDarklyFlags;
  showTrialBanner: boolean;
  isTrialExpired: boolean;
  daysLeft: number;
  handleDismissBanner: () => void;
  isEligibleForExtension: boolean;
  latestTrialExtension?: TrialExtension;
  showCancelledBanner: boolean;
  handleDismissCancelledBanner: () => void;
  isActiveCancelledSubscription: boolean;
}) => {
  const dispatch = useDispatch();
  const headerRef = React.useRef<HTMLElement>(null);
  const state = useShallowSelector(headerSelector);
  const { emailConfig, envelopeId, blocks, blockGroups } = state;
  const isFormsUser = state.settings?.account.userOrigin === UserOrigins.Forms;
  const areTemplatesEnabled = props.flags && props.flags['enable-templates-access'];
  const hideUIItems = props.flags && props.flags['hide-build-ui-elements'];
  const headerMarginRef = React.useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [appMenuOpen, setAppMenuOpen] = React.useState(false);
  const [helpMenuOpen, setHelpMenuOpen] = React.useState(false);
  const [showEditEmailModal, setShowEditEmailModal] = React.useState(false);
  const { content } = useContext(HeaderContext);
  const open = Boolean(anchorEl);
  const classes = useStyles(props);
  const history = useHistory();
  const isSmallScreen = useIsSmallScreenMediaQuery();
  const envelopeSent = state.envelopeStatus.length && state.envelopeStatus === EnvelopeStatuses.SENT;
  const isEnvelopeResource = state.resource === ResourceNames.ENVELOPE;
  // We set the envelope id after the fetch call on the build ui
  const isBuildPage = !!state.id && window.location.pathname.endsWith(state.id);

  const isMobileApp = useIsSkySlopeMobileApp();
  const { theme } = useSearchParams();
  const isAgnosticTheme = theme === 'agnostic';

  React.useEffect(() => {
    if (!isMobileApp) {
      const headerHeight = headerRef.current!.clientHeight;
      headerMarginRef.current!.style.height = `${headerHeight}px`;
      props.setHeaderSize(headerHeight);
    }
  });

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const toggleModal = () => {
    const emailSubject = emailConfig.subject?.trim() || '';
    const emailBody = emailConfig.body?.trim() || '';
    if (hideUIItems) {
      const updatedBlocksAndGroups = validateBlocksAndSendEnvelope(blocks, blockGroups, true, state.zoom);
      if (updatedBlocksAndGroups) {
        dispatch(
          actions.validateBlocksBeforeSending(
            updatedBlocksAndGroups.updatedBlocks,
            updatedBlocksAndGroups.updatedGroups,
            { subject: emailSubject || '', body: emailBody },
            true
          )
        );
      } else if (envelopeId) {
        dispatch(actions.updateEmailConfig(envelopeId, { subject: emailSubject, body: emailBody }, true));
      }
    } else {
      setShowEditEmailModal(!showEditEmailModal);
    }
  };

  function handleHelpClick() {
    setHelpMenuOpen(!helpMenuOpen);
  }

  function handleAppItemClick() {
    setAppMenuOpen(!appMenuOpen);
  }

  function handleMenuBackButton() {
    if (helpMenuOpen) {
      setHelpMenuOpen(false);
    } else {
      setAppMenuOpen(false);
    }
  }

  function handleClick(event: React.MouseEvent<HTMLButtonElement>) {
    setAnchorEl(event.currentTarget);
  }

  function handleClose() {
    setAnchorEl(null);
  }

  function mobileDrawerListItems() {
    if (appMenuOpen) {
      return <SkyslopeAppsMenuItems />;
    }
    if (helpMenuOpen) {
      return (
        <>
          <ListItem disableGutters>
            <span className={classes.itemContainer}>
              <ListItemText classes={{ root: classes.listItemText }} primary="Help" />
            </span>
          </ListItem>
          <MenuItem
            className={classes.menuItem}
            onClick={() => window.open('https://skyslope.com/privacy-policy/', '_blank')}
            disableGutters
            classes={{
              root: classes.menuItemRoom,
            }}
          >
            <span className={classes.itemContainer}>
              <ListItemText primary="Privacy Policy" />
            </span>
          </MenuItem>
          <MenuItem
            classes={{ root: classes.menuItemRoom }}
            className={classes.menuItem}
            onClick={() => window.open('https://skyslope.com/terms-conditions/', '_blank')}
            disableGutters
          >
            <span className={classes.itemContainer}>
              <ListItemText primary="Terms of Use" />
            </span>
          </MenuItem>
        </>
      );
    }

    return (
      <>
        {!isFormsUser && (
          <>
            <MenuItem
              component="a"
              href="/envelopes"
              disableGutters
              className={classes.menuItem}
              classes={{ root: classes.menuItemRoom }}
            >
              <span
                style={{ visibility: isEnvelopeResource ? 'visible' : 'hidden' }}
                className="h-20 w-1 rounded-r-sm rounded-l-none bg-blue-800 absolute"
              />
              <span
                className={cs(classes.itemContainer, {
                  [classes.activeSection]: isEnvelopeResource,
                })}
              >
                <ListItemIcon>
                  <MailIcon className="mr-4 text-blue-800 h-6 w-6" />{' '}
                </ListItemIcon>
                <ListItemText className={isEnvelopeResource ? classes.activeText : ''} primary="Envelopes" />
              </span>
            </MenuItem>
            {areTemplatesEnabled && (
              <MenuItem
                className={classes.menuItem}
                component="a"
                href="/templates"
                disableGutters
                classes={{ root: classes.menuItemRoom }}
              >
                <span
                  style={{ visibility: !isEnvelopeResource ? 'visible' : 'hidden' }}
                  className="h-20 w-1 rounded-r-sm rounded-l-none bg-blue-800 absolute"
                />
                <span
                  className={cs(classes.itemContainer, {
                    [classes.activeSection]: !isEnvelopeResource,
                  })}
                >
                  <ListItemIcon>
                    <DescriptionIcon className="mr-4 text-blue-800 h-6 w-6" />{' '}
                  </ListItemIcon>
                  <ListItemText className={!isEnvelopeResource ? classes.activeText : ''} primary="Templates" />
                </span>
              </MenuItem>
            )}
          </>
        )}
        <MenuItem
          className={classes.menuItem}
          disableGutters
          classes={{ root: classes.menuItemRoom }}
          onClick={handleAppItemClick}
        >
          <span className={classes.itemContainer}>
            <ListItemIcon>
              <AppsIcon className="mr-4 text-blue-800 h-6 w-6" />
            </ListItemIcon>
            <ListItemText primary="Apps" />
          </span>
        </MenuItem>
        <MenuItem
          className={classes.menuItem}
          disableGutters
          classes={{ root: classes.menuItemRoom }}
          onClick={handleHelpClick}
        >
          <span className={classes.itemContainer}>
            <ListItemIcon>
              <HelpIcon className="mr-4 text-blue-800 h-6 w-6" />
            </ListItemIcon>
            <ListItemText primary="Help" />
          </span>
        </MenuItem>
      </>
    );
  }

  function handleEditEmailMessage() {
    handleClose();
    setShowEditEmailModal(true);
  }

  function exit() {
    if (isFormsUser) {
      confirmNavigationAwayFromPage();
      window.history.back();
    } else {
      history.push(isEnvelopeResource ? '/envelopes' : '/templates');
    }
  }

  function moreMenuItems() {
    return (
      <span>
        {envelopeSent ? null : <MenuItem onClick={handleEditEmailMessage}>Edit Message</MenuItem>}
        {isFormsUser ? <MenuItem onClick={exit}>Save & Exit</MenuItem> : <MenuItem onClick={exit}>Exit</MenuItem>}
      </span>
    );
  }

  function headerContent() {
    const headerClass = isSmallScreen ? 'w-full h-[135px]' : classes.headerMargin;
    return (
      <div className={headerClass} ref={headerMarginRef}>
        {!isMobileApp && (
          <>
            <CollapseOnScroll disableCollapse={!isSmallScreen || (isSmallScreen && props.showTrialBanner)}>
              <div
                className={classes.headerMargin}
                ref={headerMarginRef}
                style={{ height: isSmallScreen ? '64px' : 'auto' }}
              />
            </CollapseOnScroll>
            <CollapseOnScroll disableCollapse={!isSmallScreen || (isSmallScreen && props.showTrialBanner)}>
              <MainHeader
                classes={classes}
                isSmallScreen={isSmallScreen}
                headerMarginRef={headerMarginRef}
                headerRef={headerRef}
                envelopeSent={envelopeSent}
                isFormsUser={isFormsUser}
                handleClick={handleClick}
                anchorEl={anchorEl}
                handleClose={handleClose}
                open={open}
                toggleModal={toggleModal}
                setHelpMenuOpen={setHelpMenuOpen}
                setAppMenuOpen={setAppMenuOpen}
                helpMenuOpen={helpMenuOpen}
                showEditEmailModal={showEditEmailModal}
                moreMenuItems={moreMenuItems}
                handleMenuBackButton={handleMenuBackButton}
                appMenuOpen={appMenuOpen}
                listItems={mobileDrawerListItems}
                content={content}
                redirectHeader={props.redirectHeader}
                showTrialBanner={props.showTrialBanner}
                isTrialExpired={props.isTrialExpired}
                daysLeft={props.daysLeft}
                handleDismissBanner={props.handleDismissBanner}
                isMobileApp={isMobileApp}
                isEligibleForExtension={props.isEligibleForExtension}
                latestTrialExtension={props.latestTrialExtension}
                showCancelledBanner={props.showCancelledBanner}
                handleDismissCancelledBanner={props.handleDismissCancelledBanner}
                isActiveCancelledSubscription={props.isActiveCancelledSubscription}
                isAgnosticTheme={isAgnosticTheme}
              />
            </CollapseOnScroll>
          </>
        )}
        {(isSmallScreen || isMobileApp) &&
        !state.readyToSendModalOpen &&
        isBuildPage &&
        !state.envelopeSent &&
        !state.saving ? (
          <MobileRecipientsHeader />
        ) : null}
      </div>
    );
  }

  return headerContent();
};

const MainHeader = forwardRef((props: any, ref: React.RefObject<any>) => {
  const {
    classes,
    isSmallScreen,
    headerRef,
    envelopeSent,
    isFormsUser,
    handleClick,
    anchorEl,
    handleClose,
    open,
    toggleModal,
    setHelpMenuOpen,
    setAppMenuOpen,
    helpMenuOpen,
    showEditEmailModal,
    moreMenuItems,
    handleMenuBackButton,
    appMenuOpen,
    listItems,
    content,
    showTrialBanner,
    isTrialExpired,
    daysLeft,
    handleDismissBanner,
    isMobileApp,
    isEligibleForExtension,
    latestTrialExtension,
    showCancelledBanner,
    handleDismissCancelledBanner,
    isActiveCancelledSubscription,
    isAgnosticTheme,
  } = props;

  const history = useHistory();
  const isTemplatePage = /\/templates/.test(window.location.toString());

  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = React.useState(false);

  function handleRedirect() {
    if (props.redirectHeader) {
      history.push(isTemplatePage ? '/templates' : '/envelopes');
    }
  }

  // temporary fix for mobile header for subscriptions page
  const isSubscriptionsPage = window.location.pathname.endsWith('subscriptions');

  const subscriptionBanners = (
    <>
      {showTrialBanner && !isMobileApp && !isSubscriptionsPage && (
        <TrialPeriodBanner
          expired={isTrialExpired}
          daysLeft={daysLeft}
          dismissBanner={() => handleDismissBanner()}
          isEligibleForExtension={isEligibleForExtension}
          latestTrialExtension={latestTrialExtension}
        />
      )}
      {showCancelledBanner && !isMobileApp && !isSubscriptionsPage && (
        <CancelledSubscriptionBanner
          dismissBanner={() => handleDismissCancelledBanner()}
          isActiveCancelled={isActiveCancelledSubscription}
        />
      )}
    </>
  );

  const appBarClassName = twMerge('bg-white shadow h-auto transition-all', isSmallScreen && 'h-24 flex justify-around');

  if (isAgnosticTheme) {
    return (
      <AppBar position="fixed" className={appBarClassName} ref={mergeRefs(headerRef, ref)}>
        <div className="flex items-center justify-center w-full min-h-[71px] shadow-md">
          <img src={skyslopeLogo} alt="SkySlope" width={146} height={30} />
        </div>
        {subscriptionBanners}
      </AppBar>
    );
  }

  return (
    <AppBar position="fixed" className={appBarClassName} ref={mergeRefs(headerRef, ref)}>
      <div className="flex flex-row justify-between sm:py-3 sm:px-6">
        <div className={isSmallScreen ? 'flex w-full flex-col' : 'flex w-full items-center'}>
          <div className={isSmallScreen ? 'flex justify-between px-2.5' : ''} id="logoContainer">
            {isSmallScreen && (
              <IconButton onClick={() => setIsMobileDrawerOpen((o) => !o)}>
                <MenuIcon />
                <span className="sr-only">Open menu</span>
              </IconButton>
            )}
            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */}
            <img
              src={digiSign}
              alt="DigiSign"
              className={cs(
                'w-[120px] max-w-[120px]',
                !isSmallScreen && 'mb-4',
                props.redirectHeader && 'cursor-pointer'
              )}
              onClick={() => handleRedirect()}
            />

            {isSmallScreen && (
              <>
                <IconButton
                  style={{ visibility: !!envelopeSent || isFormsUser ? 'hidden' : 'unset' }}
                  onClick={handleClick}
                >
                  {!isSubscriptionsPage && <MoreHorizIcon />}
                </IconButton>
              </>
            )}
          </div>
          {isSubscriptionsPage ? null : (
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              MenuListProps={{ 'aria-labelledby': 'basic-button' }}
            >
              {moreMenuItems()}
            </Menu>
          )}
          {showEditEmailModal && (
            <EditEmailModal
              fullScreen={isSmallScreen}
              onClose={toggleModal}
              subject="Ready to send?"
              action="Send"
              send
            />
          )}
          <Dialog
            classes={{
              root: 'flex',
              paper: 'm-0 h-full w-full',
              paperScrollPaper: 'max-h-none',
              scrollPaper: 'w-full max-w-[320px]',
            }}
            open={isMobileDrawerOpen}
            scroll="paper"
            onClose={() => {
              setIsMobileDrawerOpen(false);
              setHelpMenuOpen(false);
              setAppMenuOpen(false);
            }}
          >
            <DialogTitle classes={{ root: classes.titleRoot }}>
              {appMenuOpen || helpMenuOpen ? (
                <ArrowBackIcon onClick={handleMenuBackButton} />
              ) : (
                <img src={digiSign} alt="logo" className="w-[120px] max-w-[120px]" />
              )}
              <IconButton
                style={{ zIndex: 1500, color: colors.grey[500] }}
                onClick={() => {
                  setIsMobileDrawerOpen(false);
                  setAppMenuOpen(false);
                  setHelpMenuOpen(false);
                }}
                aria-label="close"
              >
                <span className="sr-only">Close</span>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent style={{ padding: 0 }}>
              <List>{listItems()}</List>
            </DialogContent>
            <DialogActions onClick={() => handleAppLogout()} classes={{ root: classes.actions }}>
              <ExitToAppIcon style={{ paddingRight: '10px' }} /> Logout
            </DialogActions>
          </Dialog>
          {content ? <div className="ml-8 w-full">{content}</div> : null}
        </div>
      </div>
      {subscriptionBanners}
    </AppBar>
  );
});

// eslint-disable-next-line import/no-default-export -- This code was written prior to enforcing lint rules
export default withLaunchDarkly(Header);
